import { useState } from 'react';
import { useLinkTo } from '@react-navigation/native';
import { URLS } from 'src/constants/urls';
import { useDispatch, useSelector } from 'react-redux';
import {
  setFilledTabsIndex,
  setActiveTab,
  setShowGlobalLoader,
  setLastQuoteScreen,
} from 'src/slices/UiSlice';
import { TABS_INDEX } from 'src/constants/tabs';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';
import {
  useLazySendApplicationIdToGenerateQuoteQuery,
  useLazyGetIndicationQuery,
  useLazySendApplicationIdToGenerateAMICQuoteQuery,
} from 'src/features/application/api/applicationApi';
import { useGetTopCarriersMutation } from 'src/features/carrier/carrierEngineApi';
import { RootState } from 'src/store/store';

export const useConfirmation = () => {
  const accountType = useSelector((state: RootState) => state.UI.accountType);
  const application = useSelector((state: RootState) => state.application.application);
  const currentCompanyId = useSelector((state: RootState) => state.company.company?.id);

  //lazy query
  const [triggerQuery, { isLoading }] = useLazySendApplicationIdToGenerateQuoteQuery();
  const [triggerIndicationQuery, { isLoading: isIndicationLoading }] = useLazyGetIndicationQuery();
  const [triggerAMICQuery, { isLoading: isAMICLoading }] =
    useLazySendApplicationIdToGenerateAMICQuoteQuery();
  const [getTopCarriers, { isLoading: isCarriersLoading }] = useGetTopCarriersMutation();

  const [isChecked, setIsChecked] = useState(false);
  const linkTo = useLinkTo();
  const dispatch = useDispatch();
  const finalLoading: boolean =
    isLoading || isIndicationLoading || isAMICLoading || isCarriersLoading;

  const handleCheckboxChange = (checked: boolean) => {
    setIsChecked(checked);

    // Track checkbox click event
    trackGTMEvent(
      'acknowledge-checkbox-pressed',
      {
        userEvent: `User clicked Acknowledge checkbox to confirm details`,
      },
      accountType,
    );
  };

  const navigateToPolicy = () => {
    trackGTMEvent(
      'acknowledgement-screen-left',
      {
        userEvent: 'User clicked Back and left the Acknowledgement screen',
      },
      accountType,
    );

    linkTo(URLS.APPLICATION.LOSS);
  };

  const navigateToQuote = async () => {
    trackGTMEvent(
      'acknowledgement-given',
      {
        userEvent: 'User clicked Next on the Acknowledgement screen',
      },
      accountType,
    );

    if (application?.id) {
      try {
        dispatch(setShowGlobalLoader(true));

        let error;
        let hasCoverwhale = false;
        let hasTenFour = false;
        let hasAMIC = false;

        // Only check carriers and generate quotes if we have a company ID
        if (currentCompanyId) {
          // Get top 5 carriers first
          const { data: topCarriers } = await getTopCarriers({
            companyId: currentCompanyId,
            k: 5,
          });

          // Check which carriers are in top 5
          hasCoverwhale =
            topCarriers?.some((carrier) => {
              const carrierNameLower = carrier.carrierName?.toLowerCase();
              return carrierNameLower?.includes('cover whale');
            }) || false;

          hasTenFour =
            topCarriers?.some((carrier) => {
              const carrierNameLower = carrier.carrierName?.toLowerCase();
              return carrierNameLower?.includes('ten four');
            }) || false;

          hasAMIC =
            topCarriers?.some((carrier) => {
              const carrierNameLower = carrier.carrierName?.toLowerCase();
              return (
                carrierNameLower?.includes('amic') || carrierNameLower?.includes('american modern')
              );
            }) || false;

          // Collect all carrier quote generation promises for parallel execution
          const quotePromises = [];

          if (hasCoverwhale) {
            quotePromises.push(
              triggerQuery(application.id).then((result) => ({
                carrier: 'CoverWhale',
                result,
              })),
            );
          }

          if (hasTenFour) {
            quotePromises.push(
              triggerIndicationQuery(application.id).then((result) => ({
                carrier: 'TenFour',
                result,
              })),
            );
          }

          if (hasAMIC) {
            quotePromises.push(
              triggerAMICQuery(application.id).then((result) => ({
                carrier: 'AMIC',
                result,
              })),
            );
          }

          // Execute all quote generations in parallel
          if (quotePromises.length > 0) {
            const quoteResults = await Promise.allSettled(quotePromises);

            // Process results
            const allQuotes = [];
            quoteResults.forEach((settled) => {
              if (settled.status === 'fulfilled') {
                const { carrier, result } = settled.value;
                if (result.error) {
                  console.error(`${carrier} quote generation error:`, result.error);
                  // Track CoverWhale errors for navigation logic
                  if (carrier === 'CoverWhale') {
                    error = result.error;
                  }
                } else if (result.data) {
                  allQuotes.push(result.data);
                }
              }
            });

            // Dispatch all quotes to Redux (CoverWhale quote already dispatched by RTK Query onQueryStarted)
            // Additional quotes from AMIC can be merged here if needed
            console.log(`Successfully generated ${allQuotes.length} quote(s)`);
          } else {
            console.log(
              'No carriers (CoverWhale, TenFour, or AMIC) in top 5, skipping quote generation',
            );
          }
        } else {
          console.log('No company ID available, skipping carrier check and quote generation');
        }

        dispatch(setActiveTab({ activeTab: TABS_INDEX.QUOTES }));
        dispatch(setFilledTabsIndex({ filledTabsIndex: TABS_INDEX.QUOTES }));

        const isEligible =
          application.nonTruckingLiabilityRequested === true &&
          application.autoLiabilityRequested === false;

        // Only CoverWhale routes to /quote (purchasable quotes)
        // AMIC is fire-and-forget (async automation), always goes to /quote-success
        if (isEligible && hasCoverwhale && !error) {
          // Eligible for self-purchase, go to quotes screen
          dispatch(setLastQuoteScreen('PURCHASE'));
          linkTo(URLS.QUOTE.PURCHASE);
        } else if (isEligible && hasCoverwhale && error) {
          // If the CoverWhale quote generation failed, navigate to success screen
          dispatch(setLastQuoteScreen('SUCCESS'));
          linkTo(URLS.QUOTE.SUCCESS);
        } else {
          // Not eligible or no CoverWhale, go directly to success screen
          dispatch(setLastQuoteScreen('SUCCESS'));
          linkTo(URLS.QUOTE.SUCCESS);
        }
      } catch (err) {
        console.log('Error creating a coverwhale quote:', err);
        dispatch(setShowGlobalLoader(false));
        dispatch(setActiveTab({ activeTab: TABS_INDEX.QUOTES }));
        dispatch(setFilledTabsIndex({ filledTabsIndex: TABS_INDEX.QUOTES }));
        dispatch(setLastQuoteScreen('SUCCESS'));
        linkTo(URLS.QUOTE.SUCCESS);
      }
    }
  };

  return {
    isChecked,
    handleCheckboxChange,
    navigateToPolicy,
    navigateToQuote,
    isLoading: finalLoading,
  };
};
